import { PROTOCOL_TEST_IDS } from '@/components/admin/protocoles/contracts';
import { TooltipShield } from '@/components/admin/tooltip-shield';
import type { ProtocolStepDraft } from '@matter_extractor/shared';
import { Checkbox, Tooltip } from '@pms-digital/design-system';
import { useId } from 'react';

/**
 * The checkbox « À l'entrée, relire l'étape d'où l'on vient » (issue #165,
 * §7.2; the second box the maquette `adieu/etape-adieu.html`, variante A,
 * reserved under « Étape d'adieu »).
 *
 * Ticked on the ARRIVING step: entering it, the scribe rereads once the last
 * pass of the step just left, with this step's objectives as the grid, and an
 * objective whose criteria and quantity pass is succeeded on entry (§8.1,
 * §8.3). The source is never chosen here - it is always the step one comes
 * from, by the order, a jump or a repli - so the box is all there is to set.
 *
 * ## Closed on a farewell step, and unticked by it
 *
 * « Elle est fermée sur une étape d'adieu, qui n'a pas d'objectifs » (H93): the
 * box is DISABLED there with the reason in a bubble, and ticking « Étape
 * d'adieu » unticks it (`setFarewell`), so the editor never writes the pair
 * `protocolProblems` refuses as `reread-on-farewell-step`. As on the farewell
 * box, a step that ALREADY carries the pair - a content posed by hand or by the
 * assistant - keeps the box live, since unticking is the only way out of the
 * refusal.
 *
 * The bubble hangs on `TooltipShield` for the reason `farewell-option.tsx`
 * gives: a disabled control receives neither pointer nor focus.
 *
 * No badge on the frise card (H105): the case reads in the step's own panel.
 */

export interface RereadOptionProps {
  readonly step: ProtocolStepDraft;
  readonly readOnly: boolean;
  readonly onChangeStep: (next: ProtocolStepDraft) => void;
}

/** Why the box is greyed - §7.2, word for word. */
export const REREAD_BLOCKED_REASON = 'Une étape d’adieu n’a pas d’objectifs à remplir.';

export function RereadOption({ step, readOnly, onChangeStep }: RereadOptionProps) {
  const id = useId();
  // Blocked by the step's NATURE, which is what the bubble explains; read-only
  // is said above the whole editor and gets no bubble of its own.
  const blocked = step.farewell && !step.rereadOnEntry;
  const closed = readOnly || blocked;
  const explained = blocked && !readOnly;

  const option = (
    <TooltipShield closed={explained} layout="items-start">
      <Checkbox
        id={id}
        data-testid={PROTOCOL_TEST_IDS.stepReread}
        checked={step.rereadOnEntry}
        disabled={closed}
        onCheckedChange={(checked) => {
          // A closed box writes nothing, whatever event arrives - the
          // guard `farewell-option.tsx` explains (jsdom still delivers a
          // synthetic click to a disabled input).
          if (closed) return;
          onChangeStep({ ...step, rereadOnEntry: checked });
        }}
        label="À l’entrée, relire l’étape d’où l’on vient"
        description="Ce que la personne a déjà dit à l’étape précédente peut remplir les objectifs de celle-ci."
      />
    </TooltipShield>
  );
  // Only a box closed for a reason carries a bubble: the package bubble has no
  // « empty label, stay shut » of its own.
  return explained ? <Tooltip content={REREAD_BLOCKED_REASON}>{option}</Tooltip> : option;
}
